import React, { useState, useEffect } from 'react'
import Modal, { ModalBody, ModalHeader } from 'core-system/Modal'
import Text from 'core-system/Text'
import Loading from 'core-system/Loading'
import SearchInput from 'core-system/SearchInput'
import Divider from 'core-system/Divider'
import styled from 'styled-components'
import { screenSizes } from 'shared/RandomUtils'

const MinWidthModalBody = styled(ModalBody)`
  @media (min-width: ${screenSizes.mobile}px) {
    min-width: 30rem;
    text-align: center;
  }
`

const ProviderListContainer = styled.div`
  height: 30rem;
`

const ProviderListItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 1rem 0;
  cursor: pointer;
  &:hover {
    font-weight: 600;
  }
`
const ProviderListItemIcon = styled.img`
  width: 2rem;
  height: 2rem;
  margin-right: 1rem;
`

// Generic interface for items that can be displayed in the modal
interface SelectableItem {
  id: string
  [key: string]: any
}

export interface GenericSelectModalProps<T extends SelectableItem> {
  open: boolean
  onClose: (e: React.MouseEvent<any>) => void
  onSelect: (item: T) => void
  items: T[]
  getDisplayName: (item: T) => string
  getIcon: (item: T) => string
  title: string
  searchPlaceholder?: string
  loading?: boolean
  error?: string | null
}

interface ItemsListProps<T extends SelectableItem> {
  items: T[]
  onSelect: (item: T) => void
  getDisplayName: (item: T) => string
  getIcon: (item: T) => string
}

function ItemsListComponent<T extends SelectableItem>(
  props: ItemsListProps<T>
) {
  const { items, onSelect, getDisplayName, getIcon } = props
  return (
    <ProviderListContainer>
      {items.map((item) => (
        <div key={item.id}>
          <ProviderListItem onClick={() => onSelect(item)}>
            <ProviderListItemIcon
              src={getIcon(item)}
              alt={getDisplayName(item)}
            />
            <div>{getDisplayName(item)}</div>
          </ProviderListItem>
          <Divider />
        </div>
      ))}
    </ProviderListContainer>
  )
}

const ItemsList = React.memo(ItemsListComponent) as typeof ItemsListComponent

ItemsList.displayName = 'ItemsList'

// Generic reusable modal component
export function GenericSelectModal<T extends SelectableItem>(
  props: GenericSelectModalProps<T>
) {
  const {
    open,
    onClose,
    onSelect,
    items: initialItems,
    getDisplayName,
    getIcon,
    title,
    searchPlaceholder = 'Search...',
    loading = false,
    error = null,
  } = props
  const [searchVal, setSearchVal] = useState('')
  const [items, setItems] = useState(initialItems)

  useEffect(() => {
    setItems(initialItems)
  }, [initialItems])

  const handleSearch = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(evt.target.value)
    setItems(
      initialItems.filter((item) => {
        const itemName = getDisplayName(item).toLowerCase()
        const searchTerm = evt.target.value.toLowerCase()
        return itemName.includes(searchTerm)
      })
    )
  }

  const handleSearchClear = () => {
    setSearchVal('')
    setItems(initialItems)
  }

  if (error) {
    return (
      <Modal open={open} onClose={onClose}>
        <ModalHeader title={title} />
        <MinWidthModalBody
          paddingBottom='1.5rem'
          paddingTop='1.5rem'
          paddingLeft='2rem'
          paddingRight='2rem'
        >
          <Text variant='action2'>Error loading items</Text>
          <Text variant='action2'>Please try again later!</Text>
        </MinWidthModalBody>
      </Modal>
    )
  }

  if (loading) {
    return (
      <Modal open={open} onClose={onClose}>
        <Loading />
      </Modal>
    )
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title={title} />
      <MinWidthModalBody
        paddingBottom='1.5rem'
        paddingTop='1.5rem'
        paddingLeft='2rem'
        paddingRight='2rem'
      >
        <SearchInput
          marginBottom='0.75rem'
          barBg='white'
          variant='large'
          placeholder={searchPlaceholder}
          value={searchVal}
          onsearch={handleSearch}
          onclearSearch={handleSearchClear}
          onChange={handleSearch}
          flex={1}
        />
        <ItemsList
          items={items}
          onSelect={onSelect}
          getDisplayName={getDisplayName}
          getIcon={getIcon}
        />
      </MinWidthModalBody>
    </Modal>
  )
}
